import { useNotification } from "../../components/Notification/Notification.provider";

/**
 * Hook personnalisé pour les notifications avec API simplifiée
 * 
 * @example
 * ```tsx
 * const notify = useNotify();
 * 
 * // Usage simple
 * notify.success("Opération réussie !");
 * notify.error("Une erreur est survenue");
 * notify.warning("Attention à ceci");
 * notify.info("Information importante");
 * 
 * // Usage avancé
 * const id = notify.show("Message personnalisé", "success", 5000);
 * notify.hide(id);
 * notify.hideAll();
 * ```
 */
export const useNotify = () => {
  const notification = useNotification();

  return {
    // Méthodes rapides
    success: (message: string, duration?: number) => notification.showSuccess(message, duration),
    error: (message: string, duration?: number) => notification.showError(message, duration),
    warning: (message: string, duration?: number) => notification.showWarning(message, duration),
    info: (message: string, duration?: number) => notification.showInfo(message, duration),
    
    // Méthodes complètes
    show: notification.show,
    hide: notification.hide,
    hideAll: notification.hideAll,
    
    // État
    notifications: notification.notifications,
  };
};

/**
 * Fonction utilitaire pour des notifications toast simples
 * Utile pour des appels one-shot sans hook
 */
let globalNotificationContext: ReturnType<typeof useNotification> | null = null;

export const setGlobalNotificationContext = (context: ReturnType<typeof useNotification>) => {
  globalNotificationContext = context;
};

export const toast = {
  success: (message: string, duration?: number) => {
    globalNotificationContext?.showSuccess(message, duration);
  },
  error: (message: string, duration?: number) => {
    globalNotificationContext?.showError(message, duration);
  },
  warning: (message: string, duration?: number) => {
    globalNotificationContext?.showWarning(message, duration);
  },
  info: (message: string, duration?: number) => {
    globalNotificationContext?.showInfo(message, duration);
  },
  show: (message: string, type: "info" | "error" | "success" | "warning" = "info", duration?: number) => {
    globalNotificationContext?.show(message, type, duration);
  },
  hideAll: () => {
    globalNotificationContext?.hideAll();
  },
};